import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const CartContext = createContext(null);

export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};

export const CartProvider = ({ children }) => {
    const { user } = useAuth();
    const [cart, setCart] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(false);

    const fetchCart = useCallback(async () => {
        if (!user) {
            setCart({ items: [], total: 0 });
            return;
        }
        try {
            setLoading(true);
            const response = await axios.get(`${API}/api/cart`, {
            });
            setCart(response.data);
        } catch {
            setCart({ items: [], total: 0 });
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        fetchCart();
    }, [fetchCart]);

    const addToCart = async (productId, quantity = 1) => {
        if (!user) {
            throw new Error('Devi effettuare il login');
        }
        await axios.post(`${API}/api/cart/add`, 
            { product_id: productId, quantity },
        );
        await fetchCart();
    };

    const updateQuantity = async (productId, quantity) => {
        await axios.put(`${API}/api/cart/update`,
            { product_id: productId, quantity },
        );
        await fetchCart();
    };

    const removeFromCart = async (productId) => {
        await axios.delete(`${API}/api/cart/remove/${productId}`);
        await fetchCart();
    };

    const clearCart = async () => {
        await axios.delete(`${API}/api/cart/clear`);
        setCart({ items: [], total: 0 });
    };

    const itemCount = cart.items.reduce((sum, item) => sum + item.quantity, 0);

    const value = {
        cart,
        loading,
        addToCart,
        updateQuantity,
        removeFromCart,
        clearCart,
        fetchCart,
        itemCount
    };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};
